import {
	Res,
	AuditEventType,
	CardTransactionDTO
} from 'core/api';

import * as CardProcessor from './card-processor';
import * as CashControl from './cash-control';
import { Guid, Result } from 'core/infrastructure';
import { AuditFacade, OrderingFacade } from 'pos/facades';
import * as TransactionUtils from './TransactionUtils';

import { RetryUndoPaymentScreen } from './ExternalPaymentsService.RetryUndoPaymentScreen';
import { CardTransaction, CashTransaction, DeviceType, PaymentDirection, Transaction, TransactionType, isExternalTransaction } from './ExternalPaymentsService.Common';
import { Currency, Math } from 'core/utils';


export class TransactionList {

	constructor(private readonly type: TransactionType, private readonly direction: PaymentDirection, protected readonly transactions: Transaction[] = []) {
	}

	getTransactionTypeDescription(type: TransactionType): string {
		if (type === 'Pay')
			return Res.Msg.MSG_PAYMENT_TYPE_PAY();
		if (type === 'PrePay')
			return Res.Msg.MSG_PAYMENT_TYPE_PREPAY();
		if (type === 'AddTip')
			return Res.Msg.MSG_PAYMENT_TYPE_ADD_TIP();
		if (type === 'PreRefund')
			return Res.Msg.MSG_PAYMENT_TYPE_PRE_REFUND();
		if (type === 'Refund')
			return Res.Msg.MSG_PAYMENT_TYPE_REFUND();

		return Res.Msg.MSG_PAYMENT_TYPE_OTHERS();
	}

	getType(): TransactionType {
		return this.type;
	}

	getExternalTransactions(): Transaction[] {
		return this.transactions.filter(isExternalTransaction);
	}

	hasExternalTransactions() {
		return this.getExternalTransactions().length > 0;
	}

	async addCashTransaction(ticketGlobalId: Nullable<string>, transaction: { type: TransactionType, amount: number }): Promise<Result<CashControl.TransactResult>> {
		const { amount, type } = transaction;
		const result = await CashControl.transact(amount);
		if (result.ok) {
			this.transactions.push({ device: 'CashControl', amount, type });
			await AuditFacade.audit(AuditEventType.CashControlTransaction_Registered, Res.Msg.MSG_ADD_CASH_PAYMENT(this.getTransactionTypeDescription(transaction.type), transaction.amount), new Date(), '', ticketGlobalId);
		}
		return result;
	}

	async removeCashTransaction(ticketGlobalId: Nullable<string>, amount: number): Promise<Result<CashControl.TransactResult>> {

		const previous = this.findTransaction('CashControl', amount);

		const amountToProcess = -amount;

		const result = await CashControl.transact(amountToProcess);

		if (result.ok) {
			this.transactions.remove(previous);
			const message = Res.Msg.MSG_UNDO_CASH_PAYMENT(amountToProcess, this.getTransactionTypeDescription(previous.type));
			await AuditFacade.audit(AuditEventType.CashControlTransaction_Undo, message, new Date(), '', ticketGlobalId);
		}

		return result;
	}

	async addCardTransaction(ticketGlobalId: Nullable<string>, transaction: { type: TransactionType, amount: number, askGratuity: boolean }): Promise<Result<CardTransactionDTO>> {
		const transactionIdToProcess = await OrderingFacade.registerPendingCardTransaction(ticketGlobalId, transaction.amount, transaction.type);
		let transactedXmlDetails = '';
		const result = await CardProcessor.transact({ transactionId: transactionIdToProcess, ...transaction });

		if (result.ok) {
			this.transactions.push({ device: 'CardProcessor', type: transaction.type, amount: Math.round(transaction.amount + TransactionUtils.getTip(result.value.XmlDetails), Currency.DECIMAL_PLACES), xmlDetails: result.value.XmlDetails });
			transactedXmlDetails = result.value.XmlDetails;
		}
		await OrderingFacade.finishPendingCardTransaction(ticketGlobalId, transactionIdToProcess, transaction.amount, transaction.type, transactedXmlDetails);
		return result;
	}

	async removeCardTransaction(amount: number, xmlDetails: string): Promise<Result<CardTransactionDTO>> {

		// En el caso de eliminar un pago, no se registra como pendiente en el servidor, porque no es algo que deba
		// quedar asociado al ticket, que es el objetivo de los pagos pendientes, sino algo que queremos deshacer de
		// capa de presentación. Este método se invoca cuando tengo un pago y el usuario quiere eliminarlo, pero nunca
		// un removeCardTransaction debería acabar como un pago en negativo en un ticket.

		const previous = this.findTransaction('CardProcessor', amount, xmlDetails);

		const amountToProcess = -amount;

		const type = 'Cancel' + previous.type;
		const transactionIdToProcess = Guid.create(); // Para cancelar una transacción, el id no es relevante, porque no se registra como pendiente en el servidor, pero el CardProcessor necesita un id para procesarla
		const result = await CardProcessor.transact({ transactionId: transactionIdToProcess, amount: amountToProcess, type, relatedTransactionXmlDetails: previous.xmlDetails, askGratuity: false });

		if (result.ok) {
			this.transactions.remove(previous);
		}

		return result;
	}


	async undoAll(ticketGlobalId: Nullable<string>): Promise<void> {

		if (this.transactions.length === 0)
			return;

		for (const transaction of [...this.transactions]) {
			if (transaction.device === 'CardProcessor')
				await this.undoCardPayment(ticketGlobalId, transaction);

			if (transaction.device === 'CashControl')
				await this.undoCashPayment(ticketGlobalId, transaction);
		}
	}

	private async undoCardPayment(ticketGlobalId: Nullable<string>, transaction: CardTransaction): Promise<Result<CardTransactionDTO>> {

		while (true) {
			const result = await this.removeCardTransaction(transaction.amount, transaction.xmlDetails);
			if (result.ok)
				return result;

			const { nextAction } = await RetryUndoPaymentScreen.retry(this.type, this.direction, transaction);
			if (nextAction === 'ignore-failure') {
				await this.discardCardPayment(ticketGlobalId, transaction);
				return Result.error();
			}
		}
	}

	private async undoCashPayment(ticketGlobalId: Nullable<string>, transaction: CashTransaction): Promise<Result> {

		while (true) {
			const result = await this.removeCashTransaction(ticketGlobalId, transaction.amount);
			if (result.ok)
				return Result.ok();

			const { nextAction } = await RetryUndoPaymentScreen.retry(this.type, this.direction, transaction);
			if (nextAction === 'ignore-failure') {
				await this.discardCashPayment(ticketGlobalId, transaction);
				return Result.error();
			}
		}
	}

	public async discardCardPayment(ticketGlobalId: Nullable<string>, transaction: CardTransaction): Promise<void> {

		if (!this.transactions.includes(transaction))
			throw new Error(`No existe la transacción de ${transaction.amount} en Tarjeta`);

		this.transactions.remove(transaction);
		await AuditFacade.audit(AuditEventType.CardTransaction_UndoFailed, Res.Msg.MSG_UNDO_CARD_PAYMENT_FAILED(this.getTransactionTypeDescription(transaction.type), transaction.amount, TransactionUtils.getReference(transaction.xmlDetails)), new Date(), '', ticketGlobalId);
	}

	public async discardCashPayment(ticketGlobalId: Nullable<string>, transaction: CashTransaction): Promise<void> {
		if (!this.transactions.includes(transaction))
			throw new Error(`No existe una transacción de ${transaction.amount} en Efectivo`);

		this.transactions.remove(transaction);
		await AuditFacade.audit(AuditEventType.CashControlTransaction_UndoFailed, Res.Msg.MSG_UNDO_CASH_PAYMENT_FAILED(this.getTransactionTypeDescription(transaction.type), transaction.amount), new Date(), '', ticketGlobalId);
	}

	public findTransaction(device: 'CardProcessor', amount: number, xmlDetails: string): CardTransaction;
	public findTransaction(device: 'CashControl', amount: number): CashTransaction;
	public findTransaction(device: DeviceType, amount: number, xmlDetails?: string): Transaction {

		const previous = device === 'CardProcessor'
			? this.transactions.find(x => x.device === device && x.amount === amount && x.xmlDetails === xmlDetails)
			: this.transactions.find(x => x.device === device && x.amount === amount);

		if (!previous)
			throw Error(`No se puede eliminar la transacción porque no hay ninguna transacción de tipo ${device} e importe ${amount} registrada.`);

		return previous;
	}
}
